import { IExcelReportData, ITableData, IRowCells } from 'types/excelExport';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import {
    createHeaderRow,
    createDataRow,
    createBlankRow,
    formatRateValue,
    parseNumber,
    getSourceMetricValue,
    flattenUses,
} from './helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';
import { getModelExportConfig } from './modelConfig';
import { SOURCE_METRIC_LABELS } from './constants';
import { ICreateUsesRowParams, ISourcesUsesSheetParams } from './types';

const createUsesRow = ({
    label,
    amount,
    grossSF,
    totalRentableSf,
    totalUnits,
    bold = false,
    bgColor,
    includeCostPerRsf = false,
    includeCostPerUnit = true,
    showCents = false,
}: ICreateUsesRowParams): ITableData => {
    const amountValue = amount ?? 0;
    const costPerGSF = grossSF != null && grossSF !== 0 ? amountValue / grossSF : 0;
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;

    const cells: IRowCells[] = [
        { value: label, width: 30, cellFont: bold ? { bold: true } : undefined },
        { value: amountValue, width: 18, numFmt: currencyFmt },
        { value: costPerGSF, width: 12, numFmt: EXCEL_NUM_FORMATS.CURRENCY_CENTS },
    ];

    if (includeCostPerRsf) {
        const costPerRSF = totalRentableSf != null && totalRentableSf !== 0 ? amountValue / totalRentableSf : 0;
        cells.push({ value: costPerRSF, width: 12, numFmt: EXCEL_NUM_FORMATS.CURRENCY_CENTS });
    }

    if (includeCostPerUnit) {
        const costPerUnit = totalUnits != null && totalUnits !== 0 ? amountValue / totalUnits : 0;
        cells.push({ value: costPerUnit, width: 12, numFmt: currencyFmt });
    }

    const row = createDataRow(cells, bold);

    if (bgColor) {
        row.rowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bgColor },
        };
    }

    return row;
};

const createSourceRow = (
    label: string,
    amount: number | null | undefined,
    percentOfTotal: number | null | undefined,
    loanRateType?: string | null,
    fixedRate?: number | null,
    ltc?: number | null,
    bold: boolean = false,
    bgColor?: string,
    showCents: boolean = false,
): ITableData => {
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    // For rate, use formatted string since it can be "Variable" text
    const rateValue = formatRateValue(loanRateType, fixedRate);
    const isRateNumeric = loanRateType !== 'variable' && fixedRate != null;

    const cells: IRowCells[] = [
        { value: label, width: 30, cellFont: bold ? { bold: true } : undefined },
        { value: amount ?? 0, width: 18, numFmt: currencyFmt },
        { value: percentOfTotal ?? 0, width: 12, numFmt: EXCEL_NUM_FORMATS.PERCENT },
        {
            value: isRateNumeric ? fixedRate : rateValue,
            width: 12,
            numFmt: isRateNumeric ? EXCEL_NUM_FORMATS.PERCENT : undefined,
        },
        { value: ltc ?? 0, width: 12, numFmt: EXCEL_NUM_FORMATS.PERCENT },
    ];

    const row = createDataRow(cells, bold);

    if (bgColor) {
        row.rowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bgColor },
        };
    }

    return row;
};

export const formatSourcesUsesSheet = ({
    dealName,
    usesSummary,
    sourcesSummary,
    totalUnits,
    grossSF,
    totalRentableSf,
    proFormaModelId,
    showCents = false,
}: ISourcesUsesSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proFormaModelId);
    const { sourcesUsesTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const parsedGrossSF = parseNumber(grossSF);

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: 'Sources & Uses Summary', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(4),
    ];

    // Build uses header columns
    const usesHeaderColumns = [
        sourcesUsesTab.uses.sectionHeader,
        sourcesUsesTab.uses.amountColumn,
        sourcesUsesTab.uses.costPerGsfColumn,
    ];
    const includeCostPerRsf = Boolean(sourcesUsesTab.uses.costPerRsfColumn);
    if (includeCostPerRsf) {
        usesHeaderColumns.push(sourcesUsesTab.uses.costPerRsfColumn!);
    }
    const includeCostPerUnit = Boolean(sourcesUsesTab.uses.costPerUnitColumn);
    if (includeCostPerUnit) {
        usesHeaderColumns.push(sourcesUsesTab.uses.costPerUnitColumn!);
    }

    const usesHeaderRow = createHeaderRow(usesHeaderColumns, EXCEL_COLORS.BLUE_HEADER, true);
    const usesColCount = usesHeaderColumns.length;

    const usesRows: ITableData[] = [];

    if (usesSummary?.uses) {
        const flattenedUses = flattenUses(usesSummary.uses);
        flattenedUses.forEach(({ use, level }, index) => {
            // Add empty row before high-level categories (except the first one)
            if (level === 0 && index > 0) {
                usesRows.push(createBlankRow(usesColCount));
            }
            const hasChildren = Boolean(use.children && use.children.length > 0);
            const isCategory = level === 0 || hasChildren;

            let displayName: string;
            if (hasChildren) {
                // Categories and subcategories use description
                displayName = use.description || use.nodeName || '';
            } else {
                // Budget line items use budgetLineNickname
                displayName = use.budgetLineNickname || use.nodeName || '';
            }

            // Top-level categories get dark grey, subcategories get light grey
            let bgColor: string | undefined;
            if (level === 0) {
                bgColor = EXCEL_COLORS.DARK_GRAY;
            } else if (hasChildren) {
                bgColor = EXCEL_COLORS.GRAY_HEADER;
            }

            usesRows.push(createUsesRow({
                label: displayName,
                amount: parseNumber(use.amount),
                grossSF: parsedGrossSF,
                totalRentableSf,
                totalUnits,
                bold: isCategory,
                bgColor,
                includeCostPerRsf,
                includeCostPerUnit,
                showCents,
            }));
        });
    }

    usesRows.push(createBlankRow(usesColCount));
    usesRows.push(createUsesRow({
        label: sourcesUsesTab.uses.totalLabel,
        amount: parseNumber(usesSummary?.totals?.amount),
        grossSF: parsedGrossSF,
        totalRentableSf,
        totalUnits,
        bold: true,
        bgColor: EXCEL_COLORS.DARK_GRAY,
        includeCostPerRsf,
        includeCostPerUnit,
        showCents,
    }));
    usesRows.push(createBlankRow(usesColCount));
    usesRows.push(createBlankRow(usesColCount));

    // Determine metric type from first source that has one
    const firstSourceWithMetric = sourcesSummary?.sources?.find(s => s.metricType);
    const metricType = firstSourceWithMetric?.metricType || DEVELOPMENT_SOURCE_METRIC.LTC;
    const metricLabel = SOURCE_METRIC_LABELS[metricType] || 'LTC';

    const sourcesHeaderRow = createHeaderRow([
        sourcesUsesTab.sources.sectionHeader,
        sourcesUsesTab.sources.amountColumn,
        sourcesUsesTab.sources.percentOfTotalColumn,
        sourcesUsesTab.sources.rateColumn,
        metricLabel,
    ], EXCEL_COLORS.BLUE_HEADER, true);

    const sourcesRows: ITableData[] = [];

    sourcesSummary?.sources?.forEach(source => {
        sourcesRows.push(createSourceRow(
            source.name,
            parseNumber(source.amount),
            parseNumber(source.sourcesPercentage),
            source.loanRateType,
            parseNumber(source.fixedLoanRate),
            getSourceMetricValue(source),
            false,
            undefined,
            showCents,
        ));
    });

    sourcesRows.push(createSourceRow(
        sourcesUsesTab.sources.totalLabel,
        parseNumber(sourcesSummary?.totals?.amount),
        1,
        null,
        null,
        null,
        true,
        EXCEL_COLORS.DARK_GRAY,
        showCents,
    ));

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [usesHeaderRow],
                tableData: usesRows,
            },
            {
                tableHeaderRows: [sourcesHeaderRow],
                tableData: sourcesRows,
            },
        ],
    };
};
